import React, { useEffect, useRef } from 'react';
import './ItemContextMenu.css';

/**
 * Item Context Menu
 *
 * Shows contextual actions for inventory items:
 * - Equipment: Equip/Unequip, Enhance (Star Force), Forge (Scrolls), Delete, Info
 * - Consumables: Use, Delete, Info
 * - Materials/Others: Delete, Info
 */
const ItemContextMenu = ({ item, position, onEquip, onUnequip, onUse, onDelete, onInfo, onEnhance, onForge, onCleanSlate, hasCleanSlateScroll = false, onClose, isEquipped = false }) => {
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Adjust position to stay within viewport
  useEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let { x, y } = position;

      // Adjust horizontal position
      if (x + rect.width > viewportWidth) {
        x = viewportWidth - rect.width - 10;
      }

      // Adjust vertical position
      if (y + rect.height > viewportHeight) {
        y = viewportHeight - rect.height - 10;
      }

      menuRef.current.style.left = `${x}px`;
      menuRef.current.style.top = `${y}px`;
    }
  }, [position]);

  const isEquipment = item.slot !== undefined;
  const isConsumable = item.usable === true;
  const hasForging = (item.appliedScrolls?.length || 0) > 0 || (item.failedSlots || 0) > 0;

  return (
    <div
      ref={menuRef}
      className="item-context-menu"
      style={{ left: position.x, top: position.y }}
    >
      {/* Actions only - no header */}
      <div className="item-context-actions">
        {/* Equip button (equipment in inventory) */}
        {isEquipment && !isEquipped && onEquip && (
          <button
            className="item-context-btn equip-btn"
            onClick={() => {
              onEquip(item);
              onClose();
            }}
          >
            <span className="btn-icon">⚔️</span>
            <span className="btn-label">Equip</span>
          </button>
        )}

        {/* Unequip button (equipped items) */}
        {isEquipment && isEquipped && onUnequip && (
          <button
            className="item-context-btn unequip-btn"
            onClick={() => {
              onUnequip(item);
              onClose();
            }}
          >
            <span className="btn-icon">📤</span>
            <span className="btn-label">Unequip</span>
          </button>
        )}

        {/* Enhance button (equipment only - Star Force) */}
        {isEquipment && onEnhance && (
          <button
            className="item-context-btn enhance-btn"
            onClick={() => {
              onEnhance(item);
              onClose();
            }}
          >
            <span className="btn-icon">⭐</span>
            <span className="btn-label">Enhance</span>
          </button>
        )}

        {/* Socket button (equipment only - Scroll System) */}
        {isEquipment && onForge && (
          <button
            className="item-context-btn forge-btn"
            onClick={() => {
              onForge(item);
              onClose();
            }}
          >
            <span className="btn-icon">💎</span>
            <span className="btn-label">Socket</span>
          </button>
        )}

        {/* Clean Slate button (equipment with forging, when player has scroll) */}
        {isEquipment && hasForging && hasCleanSlateScroll && onCleanSlate && (
          <button
            className="item-context-btn clean-slate-btn"
            onClick={() => {
              onCleanSlate(item);
              onClose();
            }}
          >
            <span className="btn-icon">🔄</span>
            <span className="btn-label">Clean Slate</span>
          </button>
        )}

        {/* Use button (consumables only) - does NOT close menu for spam-use */}
        {isConsumable && !isEquipment && (
          <button
            className="item-context-btn use-btn"
            onClick={() => {
              onUse(item);
              // Don't close menu - allows spam-using consumables
            }}
          >
            <span className="btn-icon">✨</span>
            <span className="btn-label">Use</span>
          </button>
        )}

        {/* Delete button (inventory items only, not equipped) */}
        {!isEquipped && (
          <button
            className="item-context-btn delete-btn"
            onClick={() => {
              onDelete(item);
              onClose();
            }}
          >
            <span className="btn-icon">🗑️</span>
            <span className="btn-label">Delete</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ItemContextMenu;
